import { useState } from "react";
import {
  X,
  CheckCircle,
  AlertTriangle,
  Wrench,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { api, ApiError, type TopicNoteReaderResponse } from "../../lib/api";
import { ShortNotesReader } from "../student/ShortNotesReader";
import { Modal } from "../ui/Modal";

export interface ShortNotesAdminPreviewModalProps {
  open: boolean;
  onClose: () => void;
  token: string | null;
  importResult: {
    chapterId?: string;
    chapterTitle?: string;
    isPublished?: boolean;
    fixes?: Array<{ path: string; fix: string; removed?: string }>;
    topics?: Array<{
      topicId: string;
      title: string;
      notes: string;
      sourcePages?: any[];
      exampleOrigin?: string;
      reviewFlags?: string[];
      wordCount?: number;
    }>;
  } | null;
  onApproved?: () => void;
}

export function ShortNotesAdminPreviewModal({
  open,
  onClose,
  token,
  importResult,
  onApproved,
}: ShortNotesAdminPreviewModalProps) {
  const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
  const [approving, setApproving] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState("");
  const [approveError, setApproveError] = useState("");

  if (!open || !importResult) return null;

  const { chapterId, chapterTitle, fixes = [], topics = [], isPublished } = importResult;
  const currentTopic = topics[selectedTopicIndex] || topics[0];

  // Build reader data shape for the currently previewed topic
  const readerData: TopicNoteReaderResponse | null = currentTopic
    ? {
        course: {
          id: null,
          name: "Module Course",
          code: "CRS",
          subject: "Study Material",
        },
        module: {
          id: "",
          title: "Module Preview",
        },
        chapter: {
          id: chapterId || "preview-chapter",
          title: chapterTitle || "Imported Chapter",
          index: 1,
          total: 1,
        },
        topic: {
          id: currentTopic.topicId,
          title: currentTopic.title,
          notes: currentTopic.notes,
        },
        position: {
          current: selectedTopicIndex + 1,
          total: topics.length,
        },
        prev:
          selectedTopicIndex > 0
            ? {
                topicId: topics[selectedTopicIndex - 1].topicId,
                title: topics[selectedTopicIndex - 1].title,
                chapterTitle: chapterTitle || "Chapter",
                startsNewChapter: false,
              }
            : null,
        next:
          selectedTopicIndex < topics.length - 1
            ? {
                topicId: topics[selectedTopicIndex + 1].topicId,
                title: topics[selectedTopicIndex + 1].title,
                chapterTitle: chapterTitle || "Chapter",
                startsNewChapter: false,
              }
            : null,
        access: "full",
      }
    : null;

  async function handleApprove() {
    if (!token || !chapterId) return;
    setApproving(true);
    setApproveError("");
    setApproveSuccess("");
    try {
      const res = await api.admin.approveChapter(token, chapterId);
      setApproveSuccess(res.message || "Chapter approved and published!");
      if (onApproved) {
        setTimeout(() => {
          onApproved();
        }, 800);
      }
    } catch (err: any) {
      setApproveError(err instanceof ApiError ? err.message : "Failed to approve chapter.");
    } finally {
      setApproving(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="xl"
      showCloseButton={false}
      className="p-0 overflow-hidden max-w-4xl max-h-[92vh]"
    >
      <div className="w-full flex flex-col overflow-hidden">
        {/* Modal Top Bar */}
        <div className="px-5 py-3.5 border-b border-border bg-card flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <h2 id="admin-preview-title" className="font-bold text-base text-foreground">
              Chapter Notes Preview
            </h2>
            <span className="text-xs font-semibold bg-secondary text-muted-foreground px-2 py-0.5 rounded">
              Admin Inspection
            </span>
            {isPublished && (
              <span className="text-xs font-semibold bg-success/10 text-success border border-success/30 px-2 py-0.5 rounded">
                Published
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {chapterId && !isPublished && (
              <button
                type="button"
                onClick={handleApprove}
                disabled={approving || !!approveSuccess}
                className="px-3.5 py-1.5 rounded bg-primary text-primary-foreground font-semibold text-xs hover:opacity-90 transition flex items-center gap-1.5 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                aria-label="Approve and publish chapter"
              >
                {approving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <CheckCircle size={13} />
                )}
                <span>{approveSuccess ? "Approved" : "Approve & publish"}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label="Close preview modal"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Success or Error Notice */}
        {approveSuccess && (
          <div className="px-5 py-2.5 bg-success/10 border-b border-success/30 text-success text-xs font-medium flex items-center gap-2">
            <CheckCircle size={14} />
            <span>{approveSuccess}</span>
          </div>
        )}
        {approveError && (
          <div className="px-5 py-2.5 bg-destructive/10 border-b border-destructive/20 text-destructive text-xs font-medium flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>{approveError}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* 1. List of Auto-fixes */}
          <section aria-labelledby="auto-fixes-title" className="p-3.5 rounded border border-border bg-card/60 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <h3 id="auto-fixes-title" className="font-semibold text-foreground flex items-center gap-1.5">
                <Wrench size={14} className="text-primary" />
                <span>Sanitization Auto-fixes ({fixes.length})</span>
              </h3>
              <span className="text-xs text-muted-foreground">
                Emojis & raw HTML stripped automatically
              </span>
            </div>

            {fixes.length > 0 ? (
              <ul className="space-y-1.5 max-h-36 overflow-y-auto admin-scroll-y pt-1 font-mono text-xs">
                {fixes.map((f, idx) => (
                  <li
                    key={idx}
                    className="p-1.5 rounded bg-secondary/70 border border-border/60 flex items-start justify-between gap-2"
                  >
                    <div>
                      <span className="font-bold text-primary">[{f.fix}]</span>{" "}
                      <span className="text-muted-foreground">{f.path}</span>
                    </div>
                    {f.removed && (
                      <span className="text-destructive font-mono truncate max-w-[200px]" title={f.removed}>
                        removed: {f.removed}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs italic">
                Clean source — no emojis or raw HTML tags needed removal.
              </p>
            )}
          </section>

          {/* 2. Topic Selector and Admin-only Flags */}
          {topics.length > 0 && (
            <section aria-label="Topic list and flags" className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Select Topic ({selectedTopicIndex + 1} of {topics.length})
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={selectedTopicIndex === 0}
                    onClick={() => setSelectedTopicIndex((i) => Math.max(0, i - 1))}
                    className="p-1 rounded border border-border text-xs disabled:opacity-40 hover:bg-secondary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                    aria-label="Previous preview topic"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    disabled={selectedTopicIndex === topics.length - 1}
                    onClick={() => setSelectedTopicIndex((i) => Math.min(topics.length - 1, i + 1))}
                    className="p-1 rounded border border-border text-xs disabled:opacity-40 hover:bg-secondary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                    aria-label="Next preview topic"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Topic chips */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {topics.map((t, idx) => {
                  const isSelected = idx === selectedTopicIndex;
                  const hasFlags = t.reviewFlags && t.reviewFlags.length > 0;
                  return (
                    <button
                      key={t.topicId || idx}
                      type="button"
                      onClick={() => setSelectedTopicIndex(idx)}
                      className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition flex items-center gap-1.5 border focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card hover:bg-secondary border-border text-foreground"
                      }`}
                    >
                      <span>{t.title}</span>
                      {hasFlags && (
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isSelected ? "bg-white" : "bg-warning"
                          }`}
                          title={`Flags: ${t.reviewFlags?.join(", ")}`}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Admin metadata for current topic */}
              {currentTopic && (
                <div className="bg-secondary/40 border border-border rounded p-3 text-xs flex flex-wrap items-center gap-4">
                  <div>
                    <span className="text-muted-foreground font-medium">Example origin:</span>{" "}
                    <span className="font-semibold text-foreground">
                      {currentTopic.exampleOrigin || "none"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-medium">Source pages:</span>{" "}
                    <span className="font-semibold text-foreground">
                      {currentTopic.sourcePages && currentTopic.sourcePages.length > 0
                        ? currentTopic.sourcePages.join(", ")
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-medium">Word count:</span>{" "}
                    <span className="font-semibold text-foreground">
                      {currentTopic.wordCount || currentTopic.notes?.split(/\s+/).length || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-muted-foreground font-medium">Review flags:</span>
                    {currentTopic.reviewFlags && currentTopic.reviewFlags.length > 0 ? (
                      currentTopic.reviewFlags.map((flag, fIdx) => (
                        <span
                          key={fIdx}
                          className="px-2 py-0.5 rounded bg-warning/10 text-warning border border-warning/30 font-semibold text-xs"
                        >
                          {flag}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 3. Pure Reader Component (same exact reader students see) */}
          <section
            aria-label="Student reader view"
            className="pt-4 border-t border-border bg-background p-4 rounded-lg"
          >
            {readerData && (
              <ShortNotesReader
                data={readerData}
                onNavigate={(tId) => {
                  const nextIdx = topics.findIndex((t) => t.topicId === tId);
                  if (nextIdx >= 0) setSelectedTopicIndex(nextIdx);
                }}
                isReadOnlyPreview={true}
              />
            )}
          </section>
        </div>
      </div>
    </Modal>
  );
}
